// The merged group view: one grid, everyone's presence on it.
//
// Together mode solves the crew as a single agent moving as one unit. Split mode
// shows each member's own solve overlaid as colored markers, which is what a crew
// actually does: together for the headliners, apart for the afternoon.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../data/store.tsx';
import { readProfileForSolver } from '../../data/preferences.tsx';
import { GridLegend, StageGrid } from '../components/StageGrid.tsx';
import { buildMemberViews } from '../../engine/normalize.ts';
import { ledgerWeights, solveTogether } from '../../engine/group.ts';
import { solveDay } from '../../engine/solver.ts';
import { buildUtilityMap } from '../../engine/utility.ts';
import { fmtRange, minutesBetween } from '../../engine/time.ts';
import type { Id, Member, PlanBlock, Profile } from '../../engine/types.ts';
import '../../styles/planning-refinements.css';

export function GroupView() {
  const { state, derived } = useApp();
  const [dayIndex, setDayIndex] = useState(0);
  const [mode, setMode] = useState<'together' | 'split'>('split');
  const ctx = derived.dayContexts[dayIndex];

  const profileFor = (userId: Id): Profile =>
    readProfileForSolver(state.profiles.find((p) => p.userId === userId), userId);

  const { presence, togetherBlocks, memberBlocks } = useMemo(() => {
    const presenceMap = new Map<Id, Member[]>();
    const perMember = new Map<Id, PlanBlock[]>();
    if (!ctx || state.members.length < 2) return { presence: presenceMap, togetherBlocks: [] as PlanBlock[], memberBlocks: perMember };

    for (const member of state.members) {
      const ratings = new Map(
        state.ratings.filter((r) => r.userId === member.userId).map((r) => [r.artistId, r]),
      );
      const mustSee = state.mustSee.filter((m) => m.userId === member.userId);
      const utility = buildUtilityMap(ctx.sets, {
        profile: profileFor(member.userId),
        ratings,
        mustSee,
        artists: derived.artistById,
        cfg: state.config,
        dayIndex,
      });
      const lockedArtists = new Set(mustSee.filter((m) => m.hardLock).map((m) => m.artistId));
      const locks = ctx.sets.filter((s) => lockedArtists.has(s.artistId)).map((s) => s.id);
      const result = solveDay({
        ctx,
        utility,
        hardLockSetIds: locks,
        rest: member.userId === state.userId ? derived.restByDay.get(ctx.day.id) ?? [] : [],
        homeStageId: 'stage-camp',
        cfg: state.config,
      });
      perMember.set(member.userId, result.blocks);
      for (const block of result.blocks) {
        if (!block.setId) continue;
        presenceMap.set(block.setId, [...(presenceMap.get(block.setId) ?? []), member]);
      }
    }

    const views = buildMemberViews(
      new Map(
        state.members.map((m) => [
          m.userId,
          { displayName: m.displayName, ratings: state.ratings.filter((r) => r.userId === m.userId) },
        ]),
      ),
      state.config,
    );
    const allLocks = state.mustSee
      .filter((m) => m.hardLock)
      .flatMap((m) => ctx.sets.filter((s) => s.artistId === m.artistId).map((s) => s.id));
    const together = solveTogether({
      ctx,
      group: {
        views,
        weights: ledgerWeights(state.ledger, state.config),
        objective: state.workspace?.settings.groupObjective ?? 'ledger',
      },
      hardLockSetIds: [...new Set(allLocks)],
      rest: derived.restByDay.get(ctx.day.id) ?? [],
      homeStageId: 'stage-camp',
      cfg: state.config,
    });

    return { presence: presenceMap, togetherBlocks: together.blocks, memberBlocks: perMember };
  }, [ctx, state, derived, dayIndex]);

  if (!ctx) return <p className="muted">No festival days are loaded.</p>;
  if (state.members.length < 2) return (
    <div className="panel planning-page planning-empty">
      <h1>Group schedules</h1>
      <p className="muted">Add another planning profile to compare your crew's suggested schedules.</p>
      <div className="planning-actions"><Link to="/profile" className="buttonlink">Manage planning crew</Link>
        <Link to="/plan" className="buttonlink">Open my plan</Link></div>
    </div>
  );

  const shown = mode === 'together' ? togetherBlocks : memberBlocks.get(state.userId) ?? [];

  return (
    <div className="cols planning-page group-plan-page">
      <div>
        <h1>Group schedules</h1>
        <p className="lede">Compare suggestions based on the crew profiles saved in this planner.</p>
        <div className="planning-group-toolbar" style={{ marginBottom: 8 }}>
          <div className="daytabs" role="group" aria-label="Festival day" style={{ margin: 0 }}>
            {derived.dayContexts.map((c, i) => (
              <button key={c.day.id} type="button" aria-pressed={i === dayIndex} className={i === dayIndex ? 'active' : ''} onClick={() => setDayIndex(i)}>
                {c.day.label}
              </button>
            ))}
          </div>
          <div className="daytabs" role="group" aria-label="Group schedule comparison" style={{ margin: 0 }}>
            <button type="button" aria-pressed={mode === 'split'} className={mode === 'split' ? 'active' : ''} onClick={() => setMode('split')}>
              Individual plans
            </button>
            <button type="button" aria-pressed={mode === 'together'} className={mode === 'together' ? 'active' : ''} onClick={() => setMode('together')}>
              Stay together
            </button>
          </div>
        </div>

        <StageGrid
          ctx={ctx}
          blocks={shown}
          artistById={derived.artistById}
          hardLockSetIds={derived.hardLockSetIds}
          presence={presence}
        />
        <GridLegend />
        <p className="muted" style={{ fontSize: 16 }}>
          {mode === 'together'
            ? 'A shared suggested route based on crew preferences and locked sets.'
            : 'Your suggested route. Dots show other members with the same suggested set.'}
        </p>
      </div>

      <div>
        <div className="panel">
          <h3>Suggested plans</h3>
          <div className="rowlist">
            {state.members.map((m) => {
              const blocks = memberBlocks.get(m.userId) ?? [];
              const music = blocks.filter((b) => b.setId);
              const walking = blocks
                .filter((b) => b.mode === 'travel')
                .reduce((a, b) => a + minutesBetween(b.startTs, b.endTs), 0);
              return (
                <div key={m.userId} className="row">
                  <span>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 9,
                        height: 9,
                        borderRadius: 999,
                        background: m.color,
                        marginRight: 7,
                      }}
                    />
                    {m.displayName}
                    <span className="sub" style={{ display: 'block' }}>
                      {music.length} sets, {walking} min walking
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="panel">
          <h3>Choices for {ctx.day.label}</h3>
          {(derived.conflictsByDay.get(ctx.day.id) ?? []).length === 0 ? (
            <p className="muted" style={{ fontSize: 16, margin: 0 }}>
              No group clashes found for this day.
            </p>
          ) : (
            <div className="rowlist">
              {(derived.conflictsByDay.get(ctx.day.id) ?? []).map((c) => (
                <div key={c.id} className="row">
                  <span>
                    {fmtRange(c.windowStart, c.windowEnd)}
                    <span className="sub" style={{ display: 'block' }}>
                      {c.memberPositions.length} members apart
                    </span>
                  </span>
                  <Link to={`/conflicts/${encodeURIComponent(c.id)}`} className="buttonlink">Compare</Link>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <h3>Plan a meetup</h3>
          <p className="muted" style={{ fontSize: 16, margin: 0 }}>
            Choose a time and a landmark with your crew. Share the details in your group conversation.
          </p>
          <Link to="/messages" className="buttonlink">Open messages</Link>
        </div>
      </div>
    </div>
  );
}
